import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import moment from "moment";
import React from "react";
import { SortingTypeI } from "../../../Utils/consts";
import {
  getBarcodeNumber,
  getClosureDate,
  getGeneratedBy,
  getGeneratedDate,
  getTrackingStatusClassName,
} from "./helpers";
import { BarcodeTrackingRow, TrackingMode } from "./types";
import Status from "Shared/Common/Status";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import useLang from "Shared/hooks/useLanguage";
import { ArrowDown, ArrowUp } from "Shared/Icons";

type TrackingTableProps = {
  loading: boolean;
  mode: TrackingMode;
  rows: BarcodeTrackingRow[];
  sort: SortingTypeI;
  onSort: (columnName: string) => void;
};

function TrackingTable({
  loading,
  mode,
  rows,
  sort,
  onSort,
}: TrackingTableProps) {
  return (
    <div className="table_bordered overflow-hidden">
      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 100px)",
          "&::-webkit-scrollbar": { width: 7 },
          "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
          "&:hover": {
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--kt-gray-400)",
              borderRadius: 2,
            },
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--kt-gray-400)",
            borderRadius: 2,
          },
        }}
        className="shadow-none"
      >
        <Table
          aria-label="supply barcode tracking"
          className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
        >
          <TableHead>
            <TableRow className="h-30px">
              <TableCell className="min-w-140px">
                <SortHeader
                  label="Status"
                  column="status"
                  sort={sort}
                  onSort={onSort}
                />
              </TableCell>
              <TableCell className="min-w-180px">
                <SortHeader
                  label="Barcode #"
                  column="barcode"
                  sort={sort}
                  onSort={onSort}
                />
              </TableCell>
              <TableCell className="min-w-180px">
                <SortHeader
                  label="Facility Name"
                  column="facilityName"
                  sort={sort}
                  onSort={onSort}
                />
              </TableCell>
              <TableCell className="min-w-200px">
                <SortHeader
                  label="Barcode Generated Date"
                  column="barcodeGeneratedDate"
                  sort={sort}
                  onSort={onSort}
                />
              </TableCell>
              <TableCell className="min-w-180px">
                <SortHeader
                  label="Barcode Generated By"
                  column="barcodeGeneratedBy"
                  sort={sort}
                  onSort={onSort}
                />
              </TableCell>
              {mode === "completed" && (
                <TableCell className="min-w-160px">
                  <SortHeader
                    label="Closure Date"
                    column="closureDate"
                    sort={sort}
                    onSort={onSort}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={mode === "completed" ? 6 : 5}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row, index) => (
                <TrackingRow
                  key={row.id ?? `${getBarcodeNumber(row)}-${index}`}
                  row={row}
                  mode={mode}
                />
              ))
            ) : (
              <NoRecord />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

function SortHeader({
  column,
  label,
  sort,
  onSort,
}: {
  column: string;
  label: string;
  sort: SortingTypeI;
  onSort: (columnName: string) => void;
}) {
  const { t } = useLang();

  return (
    <div
      onClick={() => onSort(column)}
      className="d-flex justify-content-between cursor-pointer"
    >
      <div style={{ width: "max-content" }}>{t(label)}</div>
      <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
        <ArrowUp
          CustomeClass={`${
            sort.sortingOrder === "desc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
          } p-0 m-0`}
        />
        <ArrowDown
          CustomeClass={`${
            sort.sortingOrder === "asc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
          } p-0 m-0`}
        />
      </div>
    </div>
  );
}

function TrackingRow({
  mode,
  row,
}: {
  mode: TrackingMode;
  row: BarcodeTrackingRow;
}) {
  return (
    <TableRow className="h-30px">
      <TableCell sx={{ width: "max-content", textAlign: "center" }}>
        <Status
          cusText={row.status ?? ""}
          cusClassName={getTrackingStatusClassName(row.status)}
        />
      </TableCell>
      <TableCell sx={{ width: "max-content" }}>
        {getBarcodeNumber(row)}
      </TableCell>
      <TableCell sx={{ width: "max-content" }}>{row.facilityName}</TableCell>
      <TableCell sx={{ width: "max-content" }}>
        {formatDate(getGeneratedDate(row))}
      </TableCell>
      <TableCell sx={{ width: "max-content" }}>{getGeneratedBy(row)}</TableCell>
      {mode === "completed" && (
        <TableCell sx={{ width: "max-content" }}>
          {formatDate(getClosureDate(row))}
        </TableCell>
      )}
    </TableRow>
  );
}

const formatDate = (date?: string | Date | null) =>
  date ? moment(date).format("MM-DD-YYYY") : "";

export default TrackingTable;
